import React, { useEffect, useState } from 'react'
import { SubmitHandler } from 'react-hook-form'
import { Modal } from '@node-space/storybook-components/dist/Modal'
import { logSentryErrorAndGetTraceId } from '@node-space/utils'
import { AccountContactActionError } from 'components/accountContact/AccountContactActionError'
import { AccountContactFields } from 'components/accountContact/AccountContactFields'
import {
  AccountContactErrorTypes,
  AccountContactTypes,
  PersonDefaultOptions,
} from 'components/accountContact/helpers/constants'
import {
  AccountContactData,
  AccountContactErrorType,
  AccountContactFormData,
  AccountContactPayload,
} from 'components/accountContact/helpers/types'
import { getErrorFormKey } from 'components/accountContact/helpers/utils'
import { useAccountContactForm } from 'components/accountContact/hooks/useAccountContactForm'
import { PersonSelector } from 'components/accountContact/PersonSelector'
import { setAdressFieldErrors } from 'components/AddressFormFields/utils'
import { IdVerificationMessage } from 'components/IdVerification/IdVerificationMessage'
import { useCountriesContext } from 'contexts/countriesContext'
import { useVerificationContext } from 'hooks/context/useVerificationContext'
import { useDateOfBirthInput } from 'hooks/useDateOfBirthInput'
import { useIdVerification } from 'hooks/useIdVerification'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { FormBody } from 'pages/Verification/_components/FormBody'
import { FormSubmitButton } from 'pages/Verification/_components/FormSubmitButton'
import { ApiErrorResponse, BaseErrorResponse } from 'types/errors'
import { HandleSubmit } from 'types/reactQuery'
import { AccountRepData } from './types'

const accountContactType = AccountContactTypes.ACCOUNT_REPRESENTATIVE

type HandleSaveAccountRep = HandleSubmit<AccountContactFormData, AccountContactData>

type AccountRepFormProps = AccountRepData & {
  isSavingAccountRep: boolean
  onAccountRepChange: (updatedReference: string) => void
  onSaveAccountRep: (saveAccountRepArgs: HandleSaveAccountRep) => void
}

export const AccountRepresentativeForm = ({
  initialReference,
  selectedReference,
  selectedAccountRep,
  personSelectorData,
  isSavingAccountRep,
  onAccountRepChange,
  onSaveAccountRep,
}: AccountRepFormProps) => {
  const { requestError, setRequestError, resetRequestError } = useMappedErrorMessage()
  const {
    setUnsavedChanges,
    submitStep,
    accountContacts: { refetch: refetchAccountContacts } = {},
  } = useVerificationContext()
  const { isIdVerificationEnabled, idVerificationMessage, createIdVerificationLink } =
    useIdVerification()
  const countries = useCountriesContext()

  const [idVerificationLink, setIdVerificationLink] = useState<string>()
  const [hasDateOfBirthErrors, setHasDateOfBirthErrors] = useState<boolean>()

  const isMyselfSelected = selectedReference === PersonDefaultOptions.MYSELF

  const { formatDateOfBirthInput } = useDateOfBirthInput()

  const { form, formValues, formFields, formErrors, resetForm } = useAccountContactForm({
    initialAccountContactData: selectedAccountRep,
    countryOptions: countries?.countryOptions,
    contactType: accountContactType,
  })

  const isDirty = form?.formState?.isDirty

  const errorMessage = requestError?.show ? (
    <AccountContactActionError
      errorCode={requestError?.errorCode}
      errorType={requestError?.errorType as AccountContactErrorType}
      accountContactType={accountContactType}
      contactFullName={`${formValues?.firstName} ${formValues?.lastName}`}
      sentryTraceId={requestError?.sentryTraceId}
    />
  ) : null

  const handleSubmitError = (errorType: AccountContactErrorType, error: BaseErrorResponse) => {
    const sentryTraceId = logSentryErrorAndGetTraceId(`${errorType} - ${accountContactType}`, error)
    setRequestError({
      errorCode: error?.status,
      errorType,
      sentryTraceId,
      show: true,
    })
  }

  const continueToNextStep = () => {
    refetchAccountContacts()
    submitStep()
  }

  const onCreateIdVerification = (reference: string) => {
    const createIdVerificationLinkArgs: HandleSubmit<AccountContactPayload, string> = {
      payload: {
        reference,
        type: accountContactType,
      },
      onSuccess: idVerificationLinkResponse => {
        setIdVerificationLink(idVerificationLinkResponse)
        idVerificationMessage?.setShowMessage(true)
      },
      onError: error => {
        handleSubmitError(AccountContactErrorTypes.CREATE_ID_VERIFICATION_LINK, error)
      },
    }
    createIdVerificationLink?.onCreateLink(createIdVerificationLinkArgs)
  }

  const onSubmit: SubmitHandler<typeof formValues> = (formValues: AccountContactFormData) => {
    if (!isDirty && initialReference === selectedReference) {
      continueToNextStep()
      return
    }
    resetRequestError()
    const saveAccountRepArgs: HandleSaveAccountRep = {
      payload: {
        ...formValues,
        dateOfBirth: formatDateOfBirthInput(formValues.dateOfBirth),
        address2: formValues?.address2 || null,
      },
      onSuccess: accountRepData => {
        const isExistingPerson = !!selectedAccountRep?.person?.reference
        if (isIdVerificationEnabled && !isExistingPerson) {
          onCreateIdVerification(accountRepData?.reference)
          return
        }
        continueToNextStep()
      },
      onError: (error: ApiErrorResponse) => {
        const hasAddressErrors = setAdressFieldErrors(error, getErrorFormKey, form?.setError)
        if (hasAddressErrors) return
        handleSubmitError(AccountContactErrorTypes.ADD_CONTACT, error)
      },
    }
    onSaveAccountRep(saveAccountRepArgs)
  }

  useEffect(() => {
    resetForm()
  }, [selectedAccountRep])

  useEffect(() => {
    setUnsavedChanges(isDirty)
  }, [isDirty])

  return (
    <form onSubmit={form?.handleSubmit(onSubmit)}>
      <FormBody>
        {!!errorMessage && errorMessage}
        <PersonSelector
          otherPersons={personSelectorData?.personsData}
          selectedReference={selectedReference}
          onPersonChange={onAccountRepChange}
        />
        <AccountContactFields
          contactType={accountContactType}
          savedData={selectedAccountRep}
          form={form}
          formFields={formFields}
          formValues={formValues}
          setIsDateOfBirthError={setHasDateOfBirthErrors}
        />
        <FormSubmitButton
          loading={isSavingAccountRep}
          disabled={!!Object.keys(formErrors)?.length || hasDateOfBirthErrors}
          testId="continue-button"
        />
      </FormBody>
      {idVerificationMessage?.showMessage && (
        <Modal visible closeModal={continueToNextStep}>
          <IdVerificationMessage
            contactType={accountContactType}
            {...(!isMyselfSelected && {
              contactFullName: `${formValues?.firstName} ${formValues?.lastName}`,
            })}
            linkUrl={idVerificationLink}
            onClose={continueToNextStep}
            onContinue={continueToNextStep}
          />
        </Modal>
      )}
    </form>
  )
}
